import { useCallback, useEffect, useState, type FormEvent } from "react";

import { Dialog } from "~/components/Dialog";
import {
  VerificationGhostButton,
  VerificationPrimaryButton,
} from "~/components/verification-primitives";

export type UiaDialogFlow =
  | { kind: "password"; userId: string }
  | { kind: "oauth"; url: string }
  | { kind: "unknown"; stages: string[] };

export interface UiaDialogProps {
  flow: UiaDialogFlow;
  onSubmit: (password: string) => void | Promise<void>;
  onOpen?: () => void;
  onCancel: () => void;
}

export function UiaDialog({ flow, onSubmit, onOpen, onCancel }: UiaDialogProps) {
  const [password, setPassword] = useState("");
  const [oauthOpened, setOauthOpened] = useState(false);
  const [oauthSubmitting, setOauthSubmitting] = useState(false);

  useEffect(() => {
    setPassword("");
    setOauthOpened(false);
    setOauthSubmitting(false);
  }, [flow]);

  const handleOauthSubmit = useCallback(() => {
    setOauthSubmitting(true);
    Promise.resolve(onSubmit("")).catch(() => setOauthSubmitting(false));
  }, [onSubmit]);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (flow.kind !== "password") return;
      if (!password.trim()) return;
      onSubmit(password);
      setPassword("");
    },
    [flow.kind, onSubmit, password],
  );

  const title =
    flow.kind === "password"
      ? "Verify this device"
      : flow.kind === "oauth"
        ? "Approve this device"
        : "Verification needed";
  const detail =
    flow.kind === "password"
      ? `Enter the password for ${flow.userId} to finish cross-signing this device.`
      : flow.kind === "oauth"
        ? "Open the approval page, finish the check, then return here to continue."
        : "This homeserver requested a verification method Nurion Chat cannot complete yet.";

  return (
    <Dialog
      onClose={onCancel}
      dismissOnBackdrop={false}
      labelledBy="uia-title"
      width="420px"
      panelStyle={{
        maxWidth: "calc(100vw - var(--chat-space-8))",
        borderRadius: "var(--chat-radius-lg)",
      }}
    >
      <form onSubmit={handleSubmit}>
        <div
          style={{
            padding: "var(--chat-space-6)",
            display: "flex",
            flexDirection: "column",
            gap: "var(--chat-space-4)",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--chat-space-2)" }}>
            <span
              id="uia-title"
              style={{
                color: "var(--chat-fg-primary)",
                fontFamily: "var(--chat-font-display)",
                fontSize: "var(--chat-type-display-sm-size)",
                lineHeight: "var(--chat-type-display-sm-line)",
                fontWeight: "var(--chat-type-display-sm-weight)",
              }}
            >
              {title}
            </span>
            <span
              style={{
                color: "var(--chat-fg-secondary)",
                fontFamily: "var(--chat-font-sans)",
                fontSize: "var(--chat-type-body-sm-size)",
                lineHeight: "var(--chat-type-body-sm-line)",
              }}
            >
              {detail}
            </span>
          </div>
          {flow.kind === "password" ? (
            <label
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "var(--chat-space-1)",
                color: "var(--chat-fg-secondary)",
                fontFamily: "var(--chat-font-sans)",
                fontSize: "var(--chat-type-body-sm-size)",
                lineHeight: "var(--chat-type-body-sm-line)",
              }}
            >
              Password
              <input
                autoFocus
                type="password"
                value={password}
                onChange={(event) => setPassword(event.currentTarget.value)}
                autoComplete="current-password"
                className="w-full border-0 border-b bg-transparent outline-none border-b-[var(--chat-surface-border)] focus:border-b-[var(--chat-fg-secondary)]"
                style={{
                  color: "var(--chat-fg-primary)",
                  fontFamily: "var(--chat-font-sans)",
                  fontSize: "var(--chat-type-body-sm-size)",
                  lineHeight: "var(--chat-type-body-sm-line)",
                  padding: "0 0 var(--chat-space-1)",
                }}
              />
            </label>
          ) : flow.kind === "oauth" ? (
            <div
              style={{
                color: "var(--chat-fg-muted)",
                fontFamily: "var(--chat-font-mono)",
                fontSize: "var(--chat-type-mono-size)",
                lineHeight: "var(--chat-type-mono-line)",
                overflowWrap: "anywhere",
              }}
            >
              {flow.url}
            </div>
          ) : (
            <div
              style={{
                color: "var(--chat-fg-muted)",
                fontFamily: "var(--chat-font-mono)",
                fontSize: "var(--chat-type-mono-size)",
                lineHeight: "var(--chat-type-mono-line)",
                overflowWrap: "anywhere",
              }}
            >
              {flow.stages.join(", ") || "Unknown flow"}
            </div>
          )}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "var(--chat-space-2)",
            padding: "0 var(--chat-space-6) var(--chat-space-6)",
          }}
        >
          <VerificationGhostButton onClick={onCancel}>Cancel</VerificationGhostButton>
          {flow.kind === "oauth" && (
            <>
              <VerificationGhostButton
                onClick={() => {
                  setOauthOpened(true);
                  onOpen?.();
                }}
              >
                Open approval
              </VerificationGhostButton>
              <VerificationPrimaryButton
                disabled={!oauthOpened || oauthSubmitting}
                onClick={handleOauthSubmit}
              >
                {oauthSubmitting ? "Waiting..." : "Continue"}
              </VerificationPrimaryButton>
            </>
          )}
          {flow.kind === "password" && (
            <VerificationPrimaryButton type="submit" disabled={!password.trim()}>
              Verify
            </VerificationPrimaryButton>
          )}
        </div>
      </form>
    </Dialog>
  );
}
